import { Component, inject, input } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Venue } from '../../models/venue';
import { UserHistory, MemberVenue } from '../../models/userHistory';
import { AnalyticsService } from '../../services/analytics.service';
import { VenueDataService } from '../../services/venue-data.service';

@Component({
    selector: 'app-lookup',
    imports: [CommonModule, FormsModule],
    templateUrl: './lookup.component.html',
    styleUrl: './lookup.component.css'
})
export class LookupComponent {
  private router = inject(Router);
  private functions = inject(Functions);
  private analyticsService = inject(AnalyticsService);
  private venueData = inject(VenueDataService);

  /** Embedded in the Lookup FAB panel: no page title, tighter padding, single column. */
  readonly compact = input(false);

  // Venue lookup properties
  venues$: Observable<Venue[]>;
  venueSearchTerm = '';
  private venueSearchTerm$ = new BehaviorSubject<string>('');
  selectedVenue$: Observable<Venue | null>;

  // User lookup properties
  userSearchTerm = '';
  userHistory: UserHistory | null = null;
  userSearchPerformed = false;
  isLoadingUser = false;
  userError: string | null = null;

  constructor() {
    // Shared app-wide venues stream (already name-sorted) — one Firestore
    // listener no matter how many surfaces are open.
    this.venues$ = this.venueData.getVenues();

    this.selectedVenue$ = combineLatest([this.venues$, this.venueSearchTerm$]).pipe(
      map(([venues, searchTerm]) => {
        if (!searchTerm.trim()) {
          return null;
        }
        const lowerSearchTerm = searchTerm.toLowerCase();

        // Try to find exact ID match first
        const exactIdMatch = venues.find(venue => venue.id === searchTerm);
        if (exactIdMatch) {
          return exactIdMatch;
        }

        // Then try name match
        const nameMatch = venues.find(venue =>
          venue.name?.toLowerCase().includes(lowerSearchTerm)
        );

        return nameMatch || null;
      })
    );
  }

  onVenueSearchChange(): void {
    this.venueSearchTerm$.next(this.venueSearchTerm);
    if (this.venueSearchTerm.trim()) {
      this.analyticsService.logSearch(this.venueSearchTerm, 'venue');
    }
  }

  clearVenueSearch(): void {
    this.venueSearchTerm = '';
    this.venueSearchTerm$.next('');
  }

  navigateToVenue(venueId: string): void {
    this.analyticsService.logVenueEvent('navigate', venueId);
    this.router.navigate(['/venues', venueId]);
  }

  // User lookup methods
  async onUserSearch(): Promise<void> {
    if (!this.userSearchTerm.trim()) {
      return;
    }

    this.userSearchPerformed = true;
    this.isLoadingUser = true;
    this.userError = null;
    this.userHistory = null;

    const uid = this.userSearchTerm.trim();
    this.analyticsService.logSearch(uid, 'user');

    try {
      // Call getUserHistory
      const getUserHistory = httpsCallable(this.functions, 'superAdmin-getUserHistory');
      const historyResult = await getUserHistory({ uid });

      if (historyResult.data) {
        this.userHistory = historyResult.data as UserHistory;
        this.analyticsService.logUserLookup(uid, true);
      } else {
        this.analyticsService.logUserLookup(uid, false);
      }
    } catch (error: any) {
      console.error('Error fetching user information:', error);
      this.userError = error.message || 'Failed to fetch user information. Please try again.';
      this.analyticsService.logUserLookup(uid, false);
    } finally {
      this.isLoadingUser = false;
    }
  }

  clearUserSearch(): void {
    this.userSearchTerm = '';
    this.userHistory = null;
    this.userSearchPerformed = false;
    this.isLoadingUser = false;
    this.userError = null;
  }

  formatJson(obj: any): string {
    return JSON.stringify(obj, null, 2);
  }

  // Display name from either profile: consumer (users/{uid}) or staff (system-users/{uid})
  getUserDisplayName(): string | null {
    const user = this.userHistory?.user;
    if (user?.firstName) {
      return user.lastName ? `${user.firstName} ${user.lastName}` : user.firstName;
    }
    return this.userHistory?.systemUser?.displayName || null;
  }

  getMemberVenues(): MemberVenue[] {
    return this.userHistory?.memberVenues || [];
  }
}
